import { useState, useEffect } from "react";
import { clsx } from "clsx";
import {
	LOAN_PURPOSE_BUSINESS,
	ABN_STATUS_NEW_ABN,
	LOAN_TYPE_BUSINESS_WORKING_CAPITAL,
} from "@/lib/constants";
import { useFormDataStore, useFormStateStore } from "@/components/forms/context";
import { AnimateChangeInHeight } from "@/components/animations/animateHeight";

export const AdditionalInfo = () => {
	const { formValues, setFormValue } = useFormDataStore();
	const { enabledFields, setFormError, setValidateFormDetails } = useFormStateStore();

	const {
		employmentStatus,
		abnStatus,
		gstStatus,
		creditHistory,
		combinedIncome,
		loanType,
		propertyOwner,
		propertyOwnerOther,
	} = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		employmentStatus: false,
		abnStatus: false,
		gstStatus: false,
		creditHistory: false,
		combinedIncome: false,
		propertyOwner: false,
		propertyOwnerOther: false,
	});

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormError(hasError);
	}, [errors, setFormError]);

	// Function to validate all fields
	const validateForm = () => {
		const isWorkingCapital = loanType === LOAN_TYPE_BUSINESS_WORKING_CAPITAL;

		const newErrors = {
			employmentStatus: false, //Disabled
			abnStatus: isWorkingCapital ? false : !abnStatus,
			gstStatus: isWorkingCapital || abnStatus === ABN_STATUS_NEW_ABN ? false : !gstStatus,
			creditHistory: !creditHistory,
			combinedIncome: isWorkingCapital
				? false
				: !combinedIncome || parseFloat(combinedIncome.replace(/,/g, "")) <= 0,
			propertyOwner: isWorkingCapital ? false : !propertyOwner,
			propertyOwnerOther: propertyOwner === "Other" ? !propertyOwnerOther : false,
		};

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormError(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm's Next Button can trigger this.
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [...Object.values(formValues)]);

	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		field: keyof typeof formValues
	) => {
		setFormValue(field, e.target.value);
		setErrors((prev) => ({ ...prev, [field]: !e.target.value }));
	};

	const handleSelectChange = (
		e: React.ChangeEvent<HTMLSelectElement>,
		field: keyof typeof formValues
	) => {
		setFormValue(field, e.target.value);
		setErrors((prev) => ({ ...prev, [field]: !e.target.value }));
	};

	const handleInputNumberChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		field: keyof typeof formValues
	) => {
		let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
		setFormValue(field, formattedValue.toLocaleString());
		setErrors((prev) => ({
			...prev,
			[field]: formattedValue < 10000,
		}));
	};

	return (
		<div className="bizF-space-y-2">
			{enabledFields.propertyOwner?.length > 0 && (
				<div className="bizF-flex bizF-flex-col">
					<label className="bizF-block bizF-text-md bizF-font-medium bizF-leading-6 bizF-text-gray-900">
						Are you a property owner?
					</label>
					<div className="bizF-flex bizF-flex-row bizF-gap-x-2 ">
						<div className="bizF-flex-1">
							<select
								value={propertyOwner}
								id="propertyOwner"
								aria-label="Property Owner"
								onChange={(e) => {
									handleSelectChange(e, "propertyOwner");
									if (e.target.value !== "Other") {
										setFormValue("propertyOwnerOther", "");
									}
								}}
								className={clsx(
									errors.propertyOwner
										? "bizF-select-error"
										: "bizF-select-focus",
									"bizF-select-default"
								)}
							>
								<option value="" disabled>
									Please select...
								</option>
								{enabledFields.propertyOwner?.map((lStat) => {
									return (
										<option key={lStat.id} value={lStat.value}>
											{lStat.option}
										</option>
									);
								})}
							</select>
							{errors.propertyOwner && (
								<span className="bizF-text-red-500 bizF-text-sm">
									This field is required
								</span>
							)}
						</div>

						{propertyOwner === "Other" && (
							<div className="bizF-flex-1">
								<AnimateChangeInHeight className="bizF-overflow-visible">
									<input
										value={propertyOwnerOther}
										onChange={(e) => {
											handleInputChange(e, "propertyOwnerOther");
										}}
										aria-label="Property Owner Other"
										type="text"
										id="propertyOwnerOther"
										placeholder="Property Owner Other"
										autoComplete="true"
										className={clsx(
											errors.propertyOwner
												? "bizF-input-error"
												: "bizF-input-focus",
											"bizF-input-default"
										)}
									/>
									{errors.propertyOwnerOther && (
										<span className="bizF-text-sm bizF-text-red-500">
											This field is required
										</span>
									)}
								</AnimateChangeInHeight>
							</div>
						)}
					</div>
				</div>
			)}

			{enabledFields.employmentStatus?.length > 0 &&
				formValues.loanPurpose === LOAN_PURPOSE_BUSINESS && (
					<div>
						<label className="bizF-block bizF-text-md bizF-font-medium bizF-leading-6 bizF-text-gray-900">
							What is your current employment status?
						</label>
						<select
							value={employmentStatus}
							id="employmentStatus"
							aria-label="Employment Status"
							onChange={(e) => {
								const selectedValue = e.target.value;
								setFormValue("employmentStatus", selectedValue);
								setErrors((prevErrors) => ({
									...prevErrors,
									employmentStatus: !selectedValue,
								}));
							}}
							className={clsx(
								errors.employmentStatus
									? "bizF-ring-red-500 focus:bizF-ring-red-500"
									: "bizF-ring-gray-300 focus:bizF-ring-gray-900",
								"bizF-appearance-none bizF-bg-white bizF-mt-2 bizF-block bizF-w-full bizF-rounded-md bizF-border-0 bizF-py-2.5 bizF-pl-3 bizF-pr-10 bizF-text-gray-900 bizF-ring-1 bizF-ring-inset bizF-ring-gray-300 focus:bizF-ring-1 focus-visible:bizF-outline-[--bizF-theme-color] bizF-outline-1 bizF-outline-gray-900 focus:bizF-ring-inset sm:bizF-text-md sm:bizF-leading-6"
							)}
						>
							{enabledFields.employmentStatus?.map((eStat) => {
								return (
									<option key={eStat.id} value={eStat.value}>
										{eStat.option}
									</option>
								);
							})}
						</select>
						{errors.employmentStatus && (
							<span className="bizF-text-red-500 bizF-text-sm">
								This field is required
							</span>
						)}
					</div>
				)}

			{enabledFields.abnStatus?.length > 0 &&
				loanType !== LOAN_TYPE_BUSINESS_WORKING_CAPITAL && (
					<div className="bizF-flex-1">
						<label className="bizF-label">What is your ABN status?</label>
						<div className="bizF-flex bizF-items-center bizF-space-x-2">
							<select
								value={abnStatus}
								onChange={(e) => {
									handleSelectChange(e, "abnStatus");
									if (e.target.value !== ABN_STATUS_NEW_ABN) {
										setFormValue("gstStatus", "");
									}
								}}
								id="abnStatus"
								aria-label="ABN Status"
								className={clsx(
									errors.abnStatus ? "bizF-select-error" : "bizF-select-focus",
									"bizF-select-default"
								)}
							>
								<option value="" disabled>
									Please select...
								</option>
								{enabledFields.abnStatus?.map((option) => (
									<option key={option.value} value={option.value}>
										{option.value}
									</option>
								))}
							</select>
						</div>
						{errors.abnStatus && (
							<span className="bizF-text-red-500 bizF-text-sm">
								This field is required
							</span>
						)}
					</div>
				)}

			{abnStatus !== ABN_STATUS_NEW_ABN && enabledFields.gstStatus?.length > 0 && (
				<AnimateChangeInHeight>
					<div className="bizF-flex-1">
						<label className="bizF-label">
							How long have you been registered for GST?
						</label>
						<div className="bizF-flex bizF-items-center bizF-space-x-2">
							<select
								value={gstStatus}
								onChange={(e) => {
									handleSelectChange(e, "gstStatus");
								}}
								id="gstStatus"
								aria-label="ABN Status"
								className={clsx(
									errors.gstStatus ? "bizF-select-error" : "bizF-select-focus",
									"bizF-select-default"
								)}
							>
								<option value="" disabled>
									Please select...
								</option>
								{enabledFields.gstStatus?.map((option) => (
									<option key={option.value} value={option.value}>
										{option.value}
									</option>
								))}
							</select>
						</div>
						{errors.gstStatus && (
							<span className="bizF-text-red-500 bizF-text-sm">
								This field is required
							</span>
						)}
					</div>
				</AnimateChangeInHeight>
			)}

			{enabledFields.creditHistory?.length > 0 && (
				<div>
					<label className="bizF-block bizF-text-md bizF-font-medium bizF-leading-6 bizF-text-gray-900">
						How would you rate your credit history?
					</label>
					<select
						defaultValue={creditHistory || ""}
						id="creditHistory"
						aria-label="Credit History"
						onChange={(e) => {
							handleSelectChange(e, "creditHistory");
						}}
						className={clsx(
							errors.creditHistory ? "bizF-select-error" : "bizF-select-focus",
							"bizF-select-default"
						)}
					>
						{enabledFields.creditHistory?.map((chStat) => {
							return (
								<option key={chStat.value} value={chStat.value}>
									{chStat.option}
								</option>
							);
						})}
					</select>
					{errors.creditHistory && (
						<span className="bizF-text-red-500 bizF-text-sm">
							This field is required
						</span>
					)}
				</div>
			)}

			{loanType !== LOAN_TYPE_BUSINESS_WORKING_CAPITAL && (
				<div>
					<label className="bizF-block bizF-text-md bizF-font-medium bizF-leading-6 bizF-text-gray-900">
						What is your combined annual income?
					</label>
					<div className="bizF-relative bizF-mt-2 bizF-rounded-md bizF-shadow-sm">
						<div className="bizF-pointer-events-none bizF-absolute bizF-inset-y-0 bizF-left-0 bizF-flex bizF-items-center bizF-pl-3">
							<span className="bizF-text-gray-500 sm:bizF-text-md">$</span>
						</div>
						<input
							value={combinedIncome || ""}
							type="text"
							id="grossIncome"
							aria-describedby="combinedIncome"
							inputMode="numeric"
							aria-label="Combined Income"
							onChange={(e) => {
								const value = +e.target.value.replace(/[^0-9]/g, "");
								setFormValue("combinedIncome", value.toLocaleString());
								setErrors((prevErrors) => ({
									...prevErrors,
									combinedIncome: !value,
								}));
							}}
							className={clsx(
								errors.combinedIncome
									? "bizF-ring-red-500 focus:bizF-ring-red-500"
									: "bizF-ring-gray-300 focus:bizF-ring-gray-900",
								" bizF-block bizF-w-full bizF-rounded-md bizF-border-0 bizF-py-2.5 bizF-pl-7 bizF-pr-12 bizF-text-gray-900 bizF-ring-1 bizF-ring-inset  placeholder:bizF-text-gray-400 focus:bizF-ring-1 focus-visible:bizF-outline-[--bizF-theme-color] bizF-outline-1 bizF-outline-gray-900 focus:bizF-ring-inset sm:bizF-text-md sm:bizF-leading-6"
							)}
						/>
						<div className="bizF-pointer-events-none bizF-absolute bizF-inset-y-0 bizF-right-0 bizF-flex bizF-items-center bizF-pr-3">
							<span className="bizF-text-gray-500 sm:bizF-text-sm">AUD</span>
						</div>
					</div>
					{errors.combinedIncome && (
						<span className="bizF-text-xs bizF-text-red-500">
							Combined Annual Income is required
						</span>
					)}
				</div>
			)}
		</div>
	);
};
